import { ReactNode, RefObject } from 'react'
import {
  Box,
  Drawer,
  Dialog,
  PaperProps,
  BackdropProps,
  DrawerProps as MuiDrawerProps,
  styled,
  SxProps,
} from '@mui/material'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { CHAT_LIST_APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import {
  bottomSafeAreaHeight,
  stickToBottomTransform,
  viewportHeightConsideringNotch,
} from '$setup/visual-viewport'

export const DRAWER_MAX_HEIGHT_PERCENTAGE = 80
export const PULLER_HEIGHT = 26

interface DrawerProps {
  isTransparent: boolean
  isFullscreen: boolean
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: prop =>
    prop !== 'isTransparent' && prop !== 'fullWidth' && prop !== 'fullscreen',
})<{
  isTransparent?: boolean
  fullWidth?: boolean
  isFullscreen?: boolean
}>`
  .MuiDialog-container {
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : 'unset')};
  }
  .MuiDialog-paper {
    border-radius: 15px;
    background-color: ${({ isTransparent, theme }) =>
      isTransparent ? 'transparent' : theme.palette.neutral000};
    min-width: 380px;
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '400px')};
    ${({ isFullscreen }) => ({
      ...(isFullscreen
        ? {
            maxHeight: 'auto',
            height: '100%',
            borderRadius: '0',
          }
        : {}),
    })};
  }
`

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (propName: PropertyKey) =>
    propName !== 'isTransparent' && propName !== 'isFullscreen',
})<DrawerProps>(({ isTransparent, isFullscreen }) => ({
  '&[data-stick-to-bottom="true"]': {
    transform: stickToBottomTransform,
  },
  '.MuiDrawer-paper': {
    maxHeight: `calc(${viewportHeightConsideringNotch} - ${CHAT_LIST_APP_BAR_HEIGHT}px)`,
    borderRadius: '15px 15px 0 0',
    backgroundColor: isTransparent ? 'transparent' : 'white',
    ...(isFullscreen
      ? {
          maxHeight: 'auto',
          height: '100%',
          borderRadius: '0',
        }
      : {}),

    ...(isTransparent
      ? {
          boxShadow: 'none',
        }
      : undefined),
  },
}))

const Puller = styled(Box)`
  width: 65px;
  height: 6px;
  min-height: 6px;
  background-color: #edf1f4;

  margin: 15px auto 5px auto;
  border-radius: 4px;
`

const BottomSafeAreaPlaceholder = styled('div')`
  width: 100%;
  height: ${bottomSafeAreaHeight};
  flex-shrink: 0;
`

interface Props {
  isOpen: boolean
  onClose: () => unknown
  children: ReactNode
  isTransparent?: boolean
  alwaysIsDrawer?: boolean
  variant?: MuiDrawerProps['variant']
  paperProps?: PaperProps
  bottomSafeAreaColor?: string
  className?: string
  isFullscreen?: boolean
  fullWidth?: boolean
  sx?: SxProps
  useVirtualKeyboardTransform?: boolean
  hideBackdrop?: boolean
  backdropProps?: Omit<BackdropProps, 'open'>
  hidePuller?: boolean
}

export function BottomDrawer({
  isOpen,
  onClose,
  children,
  isTransparent = false,
  alwaysIsDrawer = false,
  variant,
  paperProps,
  backdropProps,
  bottomSafeAreaColor,
  className,
  isFullscreen = false,
  fullWidth = false,
  sx,
  useVirtualKeyboardTransform = true,
  hideBackdrop,
  hidePuller = false,
}: Props) {
  const isDesktop = useIsDesktopSize()

  return !alwaysIsDrawer && isDesktop ? (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      className={className}
      PaperProps={paperProps}
      slotProps={{
        backdrop: { ...backdropProps, open: isOpen },
      }}
      hideBackdrop={hideBackdrop}
      fullWidth={fullWidth}
      isTransparent={isTransparent}
      isFullscreen={isFullscreen}
      sx={{
        '& .MuiDialog-paper': { boxShadow: isTransparent ? 'none' : undefined },
      }}
    >
      {children}
    </StyledDialog>
  ) : (
    <StyledDrawer
      data-stick-to-bottom={useVirtualKeyboardTransform}
      className={className}
      variant={variant}
      open={isOpen}
      anchor="bottom"
      onClose={onClose}
      isTransparent={isTransparent}
      PaperProps={paperProps}
      slotProps={{
        backdrop: { ...backdropProps, open: isOpen },
      }}
      isFullscreen={isFullscreen}
      sx={sx}
      hideBackdrop={hideBackdrop}
    >
      {!isTransparent && !isFullscreen && !hidePuller && <Puller />}
      {children}

      {!isDesktop && (
        <BottomSafeAreaPlaceholder
          sx={{ backgroundColor: bottomSafeAreaColor }}
        />
      )}
    </StyledDrawer>
  )
}

BottomDrawer.defaultProps = {
  isTransparent: false,
  className: null,
  bottomSafeAreaColor: 'transparent',
  isFullscreen: false,
  alwaysIsDrawer: false,
}
